import { useLiveQuery } from 'dexie-react-hooks'
import { motion } from 'framer-motion'
import {
  Activity,
  ArrowRightCircle,
  Banknote,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  FileText,
  type LucideIcon,
  MessageSquarePlus,
  Package,
  PackageCheck,
  Play,
  ShieldCheck,
  Tag,
  Timer,
  Truck,
  UserPlus,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { KpiCard } from '@/components/KpiCard'
import { ScreenHeader } from '@/components/ScreenHeader'
import { Bars, HBars } from '@/components/charts/Bars'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { useApp } from '@/context'
import { getActivitate, kpiAzi, progresLivrareAzi, topProduse, vanzariPeZi } from '@/db/queries'
import { azi, esteAzi, fmtDataLunga, fmtDataRelativa, fmtKg, fmtOra, fmtRON } from '@/lib/format'

const ACT_ICONS: Record<string, LucideIcon> = {
  MessageSquarePlus,
  CheckCircle2,
  Truck,
  PackageCheck,
  FileText,
  ShieldCheck,
  Tag,
  UserPlus,
  ArrowRightCircle,
}
const PINE_ICONS = new Set(['PackageCheck', 'CheckCircle2', 'ShieldCheck'])

export default function Panou() {
  const { runDemo } = useApp()
  const kpi = useLiveQuery(() => kpiAzi(), [])
  const activitate = useLiveQuery(() => getActivitate(9), [])
  const progres = useLiveQuery(() => progresLivrareAzi(), [])
  const produse = useLiveQuery(() => topProduse(5), [])
  const venit = useLiveQuery(() => vanzariPeZi(7), [])

  const pct = progres?.total ? Math.round((progres.livrate / progres.total) * 100) : 0

  return (
    <div>
      <ScreenHeader tab="panou">
        <span className="hidden items-center gap-2 rounded-full border border-hairline bg-card px-3 py-1.5 text-sm text-muted-foreground shadow-paper sm:inline-flex">
          <CalendarDays className="size-4 text-amber" />
          <span className="capitalize">{fmtDataLunga(azi())}</span>
        </span>
        <Button onClick={runDemo} className="gap-2">
          <Play className="size-4" />
          Simulează o comandă
        </Button>
      </ScreenHeader>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <KpiCard
          label="Comenzi azi"
          value={kpi?.comenziAzi ?? 0}
          icon={ClipboardList}
          hint="programate azi"
          index={0}
        />
        <KpiCard
          label="Kg livrate azi"
          value={kpi?.kgLivrateAzi ?? 0}
          format={fmtKg}
          icon={Package}
          hint="livrări finalizate"
          index={1}
        />
        <KpiCard
          label="Venit azi"
          value={kpi?.venitAzi ?? 0}
          format={fmtRON}
          icon={Banknote}
          hint="încasări din livrări"
          index={2}
        />
        <KpiCard
          label="În așteptare"
          value={kpi?.inAsteptare ?? 0}
          icon={Timer}
          hint="de procesat"
          index={3}
        />
        <KpiCard
          label="Mașini pe traseu"
          value={kpi?.masiniPeTraseu ?? 0}
          icon={Truck}
          hint="din flotă"
          index={4}
        />
      </div>

      {/* Main grid */}
      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Reveal index={5}>
            <Panel
              title="Venit — ultimele 7 zile"
              hint="livrări finalizate, RON/zi"
              icon={Banknote}
            >
              <Bars
                data={(venit ?? []).map((d) => ({ label: d.label, value: d.ron }))}
                formatValue={fmtRON}
              />
            </Panel>
          </Reveal>

          <div className="grid gap-5 sm:grid-cols-2">
            <Reveal index={6}>
              <Panel title="Progres livrări azi" icon={PackageCheck} className="h-full">
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-4xl font-semibold tracking-tight text-amber-ink nums">
                    {pct}%
                  </span>
                  <span className="text-sm text-muted-foreground nums">
                    {progres?.livrate ?? 0} din {progres?.total ?? 0} livrate
                  </span>
                </div>
                <Progress value={pct} className="mt-4 h-2.5" />
                <p className="mt-3 text-xs text-muted-foreground">
                  {pct === 100 && (progres?.total ?? 0) > 0
                    ? 'Toate livrările de azi au fost finalizate.'
                    : 'Comenzile rămase avansează pe flux către „Livrat".'}
                </p>
              </Panel>
            </Reveal>

            <Reveal index={7}>
              <Panel title="Top produse" hint="după kg livrate" icon={Package} className="h-full">
                {produse && produse.length > 0 ? (
                  <HBars
                    data={produse.map((p) => ({ label: p.nume, value: p.kg }))}
                    formatValue={fmtKg}
                    rowHeight={46}
                  />
                ) : (
                  <EmptyLine />
                )}
              </Panel>
            </Reveal>
          </div>
        </div>

        {/* Flux activitate */}
        <Reveal index={8} className="lg:col-span-1">
          <Panel
            title="Flux activitate"
            hint="cele mai recente evenimente"
            icon={Activity}
            className="h-full"
          >
            <ol className="space-y-1">
              {(activitate ?? []).map((a, i) => {
                const Icon = (a.icon && ACT_ICONS[a.icon]) || Activity
                const pine = a.icon ? PINE_ICONS.has(a.icon) : false
                return (
                  <motion.li
                    key={a.id}
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.35, delay: 0.5 + i * 0.05 }}
                    className="flex items-start gap-3 rounded-lg px-1.5 py-2 hairline-b last:border-0"
                  >
                    <span
                      className={`mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg ${
                        pine ? 'bg-pine-soft text-pine' : 'bg-amber-soft text-amber-ink'
                      }`}
                    >
                      <Icon className="size-3.5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm leading-snug text-foreground">{a.text}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground nums">
                        {esteAzi(a.ora)
                          ? fmtOra(a.ora)
                          : `${fmtDataRelativa(a.ora)} · ${fmtOra(a.ora)}`}
                      </p>
                    </div>
                  </motion.li>
                )
              })}
              {activitate && activitate.length === 0 && <EmptyLine />}
            </ol>
          </Panel>
        </Reveal>
      </div>
    </div>
  )
}

// ── local building blocks ────────────────────────────────────────────────────

function Panel({
  title,
  hint,
  icon: Icon,
  className,
  children,
}: {
  title: string
  hint?: string
  icon?: LucideIcon
  className?: string
  children: ReactNode
}) {
  return (
    <Card className={`gap-0 p-5 ${className ?? ''}`}>
      <div className="mb-4 flex items-center gap-2.5">
        {Icon && (
          <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-amber-soft text-amber-ink">
            <Icon className="size-4" />
          </span>
        )}
        <div className="min-w-0">
          <h2 className="font-display text-base font-medium leading-tight">{title}</h2>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
      </div>
      {children}
    </Card>
  )
}

function Reveal({
  index,
  className,
  children,
}: {
  index: number
  className?: string
  children: ReactNode
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.06, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

function EmptyLine() {
  return <p className="py-6 text-center text-sm text-muted-foreground">Nu există date încă.</p>
}
